import React, { useState, useEffect } from "react";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline";

const EditConsultantCompensation = ({ plansData, consultantCompensation, setConsultantCompensation }) => {
    const [isCollapsed, setIsCollapsed] = useState(true);
    const [compensationModel, setCompensationModel] = useState(consultantCompensation?.model || "per_employee");
    const [compensationValues, setCompensationValues] = useState(consultantCompensation?.values || {});

    // Get unique carriers from renewal and alternative plans (exclude current)
    const getUniqueCarriers = () => {
        const carriers = new Set();
        plansData.forEach(dataset => {
            if (dataset.type !== 'current') {
                carriers.add(dataset.carrier);
            }
        });
        return Array.from(carriers);
    };

    const uniqueCarriers = getUniqueCarriers();

    // Handle model change
    const handleModelChange = (model) => {
        setCompensationModel(model);
        // Reset values when changing model
        setCompensationValues({});
    };

    // Handle value change for a specific carrier
    const handleValueChange = (carrier, value) => {
        const numValue = parseFloat(value) || 0;
        setCompensationValues(prev => ({
            ...prev,
            [carrier]: numValue
        }));
    };

    // Apply changes to parent component
    const applyChanges = () => {
        setConsultantCompensation({
            model: compensationModel,
            values: compensationValues
        });
    };

    // Auto-apply changes when values change
    useEffect(() => {
        if (Object.keys(compensationValues).length > 0 || compensationModel !== consultantCompensation?.model) {
            setConsultantCompensation({
                model: compensationModel,
                values: compensationValues
            });
        }
    }, [compensationModel, compensationValues]);

    return (
        <div className="bg-white p-6 rounded-lg shadow-lg border border-gray-200 mb-6">
            <div className={`flex justify-between items-center ${isCollapsed ? "mb-0" : "mb-4"}`}>
                <div className="flex items-center space-x-3">
                    <button
                        onClick={() => setIsCollapsed(!isCollapsed)}
                        className="text-blue-600 hover:text-blue-800 focus:outline-none"
                    >
                        <span className="text-xl font-bold">
                            {isCollapsed ? (
                                <ChevronUpIcon className="h-5 w-5 text-gray-400 flex-shrink-0" />
                            ) : (
                                <ChevronDownIcon className="h-5 w-5 text-gray-400 flex-shrink-0" />
                            )}
                        </span>
                    </button>
                    <h2 className="text-xl font-bold text-gray-800">
                        Consultant Compensation (408(b)(2) Disclosure)
                    </h2>
                </div>

                {!isCollapsed && (
                    <div className="flex space-x-2">
                        <button
                            onClick={applyChanges}
                            className="px-4 py-2 rounded text-white font-medium bg-blue-500 hover:bg-blue-600 transition-colors"
                        >
                            Apply Changes
                        </button>
                    </div>
                )}
            </div>

            {!isCollapsed && (
                <>
                    {/* Model Selection */}
                    <div className="mb-6">
                        <h3 className="text-lg font-semibold text-gray-700 mb-3">Compensation Model</h3>
                        <div className="space-y-2">
                            <label className="flex items-center">
                                <input
                                    type="radio"
                                    name="compensationModel"
                                    value="per_employee"
                                    checked={compensationModel === "per_employee"}
                                    onChange={(e) => handleModelChange(e.target.value)}
                                    className="mr-2"
                                />
                                <span className="text-gray-700">Per Employee Per Month Fee</span>
                            </label>
                            <label className="flex items-center">
                                <input
                                    type="radio"
                                    name="compensationModel"
                                    value="percentage"
                                    checked={compensationModel === "percentage"}
                                    onChange={(e) => handleModelChange(e.target.value)}
                                    className="mr-2"
                                />
                                <span className="text-gray-700">% on Total Annual Premium</span>
                            </label>
                        </div>
                    </div>

                    {/* Values Input */}
                    {uniqueCarriers.length > 0 && (
                        <div>
                            <h3 className="text-lg font-semibold text-gray-700 mb-3">
                                {compensationModel === "per_employee"
                                    ? "Monthly Amount per Employee by Carrier"
                                    : "Percentage by Carrier"}
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {uniqueCarriers.map(carrier => (
                                    <div key={carrier} className="border border-gray-300 rounded-lg p-4">
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            {carrier}
                                        </label>
                                        <div className="relative">
                                            {compensationModel === "percentage" && (
                                                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">
                                                    %
                                                </span>
                                            )}
                                            {compensationModel === "per_employee" && (
                                                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">
                                                    $
                                                </span>
                                            )}
                                            <input
                                                type="number"
                                                step={compensationModel === "percentage" ? "0.1" : "0.01"}
                                                min="0"
                                                max={compensationModel === "percentage" ? "100" : undefined}
                                                value={compensationValues[carrier] || ""}
                                                onChange={(e) => handleValueChange(carrier, e.target.value)}
                                                className={`w-full ${compensationModel === "percentage" || compensationModel === "per_employee" ? "pl-8" : "pl-3"} pr-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                                                placeholder={compensationModel === "percentage" ? "0.0" : "0.00"}
                                            />
                                        </div>
                                        <p className="text-xs text-gray-500 mt-1">
                                            {compensationModel === "percentage"
                                                ? "Percentage of total annual premium"
                                                : "Monthly amount per employee"}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {uniqueCarriers.length === 0 && (
                        <div className="text-center py-8 text-gray-500">
                            <p>No carriers available for compensation calculation.</p>
                            <p className="text-sm">Upload plans with renewal or alternative carriers to configure compensation.</p>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default EditConsultantCompensation;
